import React, { useState } from "react";
import { ScoreRing } from "@/components/ScoreRing";
import { AvailabilityBadge } from "@/components/AvailabilityBadge";
import { Bookmark, BookmarkCheck, Copy, Check, ArrowUpRight, Lock, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { engineLabel } from "@/components/NamingEngineSelector";

export function NameCard({ result, index = 0, saved, onSave, onDetails, valuationLocked, onLockedValuation }) {
  const [copied, setCopied] = useState(false);
  const available = result.available;
  const hasValue = result.est_value_low != null && result.est_value_high != null;

  const copy = () => {
    navigator.clipboard?.writeText(result.name);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.5) }}
      className={`card-soft p-5 transition-all duration-200 hover:shadow-card ${
        available === false ? "opacity-[0.72] hover:opacity-100" : ""
      }`}
      data-testid={`name-card-${result.name}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-[22px] font-extrabold tracking-[-0.02em] text-ink leading-tight truncate">
            {result.name}
          </h3>
          <p className="text-[13px] text-ink-subtle font-medium mt-0.5">{result.domain}</p>
        </div>
        <ScoreRing score={result.score ?? result.commercial_score ?? 0} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3.5">
        <AvailabilityBadge available={available} extension={result.domain?.slice(result.domain.indexOf("."))} />
        {result.strategy && (
          <span className="inline-flex items-center rounded-full bg-brand-soft px-2.5 py-1 text-[11px] font-semibold text-brand" data-testid={`engine-pill-${result.name}`}>
            {engineLabel(result.strategy)}
          </span>
        )}
        {result.style && (
          <span className="inline-flex items-center rounded-full bg-canvas-alt px-2.5 py-1 text-[11px] font-semibold text-ink-muted capitalize">
            {result.style}
          </span>
        )}
      </div>

      {result.red_flag && (
        <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-warning-bg px-3 py-2 text-[12px] font-medium text-warning" data-testid={`red-flag-${result.name}`}>
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span className="leading-snug">{result.red_flag}</span>
        </div>
      )}

      {/* valuation */}
      {available === true && (
        <div className="mt-4">
          {valuationLocked ? (
            <button
              onClick={onLockedValuation}
              className="w-full flex items-center justify-between rounded-lg border border-dashed border-brand/40 bg-brand-soft/50 px-3.5 py-2.5 text-left hover:bg-brand-soft transition-colors"
              data-testid={`valuation-locked-${result.name}`}
            >
              <span className="text-[12px] font-semibold text-brand flex items-center gap-1.5">
                <Lock size={13} /> Unlock estimated value
              </span>
              <span className="text-[11px] text-brand/70 font-semibold">Upgrade</span>
            </button>
          ) : hasValue ? (
            <div className="rounded-lg bg-canvas px-3.5 py-2.5 flex items-end justify-between">
              <div>
                <p className="overline text-ink-subtle">Est. domain value</p>
                <p className="text-[20px] font-extrabold text-ink tracking-[-0.02em] leading-none mt-1">
                  ${result.est_value_low}–${result.est_value_high}
                </p>
              </div>
              {result.value_confidence && (
                <span className="text-[11px] font-semibold text-ink-subtle capitalize mb-0.5">
                  {result.value_confidence} confidence
                </span>
              )}
            </div>
          ) : null}
        </div>
      )}

      {result.reason && (
        <p className="text-[13px] text-ink-muted leading-relaxed mt-3.5 line-clamp-2">{result.reason}</p>
      )}

      <div className="flex items-center gap-2 mt-4 pt-3.5 border-t hairline">
        <button
          onClick={() => onSave?.(result)}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-[13px] font-semibold transition-colors ${
            saved ? "bg-brand-soft text-brand" : "bg-canvas-alt text-ink-muted hover:text-ink"
          }`}
          data-testid={`save-btn-${result.name}`}
        >
          {saved ? <BookmarkCheck size={15} /> : <Bookmark size={15} />}
          {saved ? "Saved" : "Save"}
        </button>
        <button
          onClick={copy}
          className="w-9 h-9 rounded-lg bg-canvas-alt text-ink-muted hover:text-ink flex items-center justify-center transition-colors"
          data-testid={`copy-btn-${result.name}`}
          title="Copy name"
        >
          {copied ? <Check size={15} className="text-success" /> : <Copy size={15} />}
        </button>
        {onDetails && (
          <button
            onClick={() => onDetails(result)}
            className="w-9 h-9 rounded-lg bg-canvas-alt text-ink-muted hover:text-ink flex items-center justify-center transition-colors"
            data-testid={`details-btn-${result.name}`}
            title="Brand details"
          >
            <ArrowUpRight size={15} />
          </button>
        )}
      </div>
    </motion.div>
  );
}
